// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ShareThis from "@mcmc/App/Components/ShareThis/ShareThis";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import {
    pdpColumnStyle,
    ProductDetailsViewStyles,
    viewStyles,
} from "@mcmc/Overrides/Widgets/ProductDetails/ProductDetailsView";
import { printIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    setBreadcrumbActions,
};

const printLinkClickHandler = () => {
    openPrintDialog();
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export const eventsViewStyles: ProductDetailsViewStyles = {
    ...viewStyles,
    detailsColumn: {
        css: [
            pdpColumnStyle,
            css`
                width: 100%;
                float: right;
                padding-right: 60px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                && {
                                    padding-right: 0;
                                }
                            `,
                            null,
                            css`
                                padding-right: 15px;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
};

const styles = eventsViewStyles;

const EventDetailsView: React.FC<Props> = ({ id, setBreadcrumbActions }) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    useEffect(() => {
        setBreadcrumbActions({
            breadcrumbActions: (
                <StyledWrapper {...styles.breadcrumbActionsWrapper}>
                    <ShareThis />

                    <Link
                        {...printIconStyle}
                        onClick={printLinkClickHandler}
                        aria-label={translate("Print Events Details")}
                    >
                        <VisuallyHidden>{translate("Print")}</VisuallyHidden>
                    </Link>
                </StyledWrapper>
            ),
        });

        return () => {
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, []);

    return (
        <>
            <StyledWrapper {...styles.layoutContainer}>
                <StyledWrapper {...styles.row}>
                    <StyledWrapper {...styles.mainContentColumn}>
                        <StyledWrapper {...styles.row}>
                            <StyledWrapper {...styles.detailsColumn}>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Title Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Title Zone" />
                                <Hidden above="md">
                                    <StyledWrapper {...styles.partNumbersBlock}>
                                        <Typography {...styles.sectionTitle}>{translate("Course Details")}</Typography>
                                        <Zone contentId={id} zoneName="Part Numbers Zone" />
                                    </StyledWrapper>
                                </Hidden>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Details Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Details Zone" />
                                <Hidden above="md">
                                    <StyledWrapper {...styles.partNumbersBlock}>
                                        <Zone contentId={id} zoneName="Availability Zone" />
                                    </StyledWrapper>
                                </Hidden>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Actions Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Actions Zone" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.partNumbersColumn}>
                        <Hidden below="lg">
                            <StyledWrapper {...styles.partNumbersBlock}>
                                <Typography {...styles.sectionTitle}>{translate("Course Details")}</Typography>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Course Number Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Part Numbers Zone" />
                            </StyledWrapper>
                        </Hidden>
                        <StyledWrapper {...styles.partNumbersBlock}>
                            {isCmsEditing && (
                                <Typography {...styles.zoneTitle}>{translate("More Information Zone")}</Typography>
                            )}
                            <Zone contentId={id} zoneName="More Information Zone" />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.tabsWrapper}>
                {isCmsEditing && <Typography {...styles.zoneTitle}>{translate("Tabs Zone")}</Typography>}
                <Zone contentId={id} zoneName="Content1" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(undefined, mapDispatchToProps)(EventDetailsView),
    definition: {
        displayName: "Event View",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
